import { Bucket, DateRange } from '../../types/Dashboard';
import { Venta } from '../../types/Altas';
import { addDays } from './dashboardHelpers';

/**
 * Cuentas para la sección "Ventas por ejecutiva" del dashboard. Reciben las
 * ventas confirmadas (ejecutiva, plan, fecha) y el rango elegido arriba.
 * Una venta de un plan para varios miembros suma uno por miembro (`cantidad`).
 */

/** Planes que suma una venta. */
export function planesDeVenta(v: Venta): number {
  return v.cantidad ?? 1;
}

/** Planes vendidos en total. */
export function totalPlanes(ventas: Venta[]): number {
  return ventas.reduce((s, v) => s + planesDeVenta(v), 0);
}

/** El rango del dashboard, recortado para que no empiece antes de la campaña (`desde`, la define el backend). */
export function rangoCampana(range: DateRange, desde: string): DateRange {
  return { from: range.from < desde ? desde : range.from, to: range.to };
}

export function ventasEnRango(ventas: Venta[], range: DateRange, desde: string): Venta[] {
  const r = rangoCampana(range, desde);
  return ventas.filter((v) => v.fecha >= r.from && v.fecha <= r.to);
}

/** Nombres únicos, ordenados por cantidad de ventas de mayor a menor. */
export function ejecutivasPorVentas(ventas: Venta[]): string[] {
  const conteo = contarPor(ventas, (v) => v.ejecutiva);
  return Object.keys(conteo).sort((a, b) => conteo[b] - conteo[a] || a.localeCompare(b, 'es'));
}

export function planesPorVentas(ventas: Venta[]): string[] {
  const conteo = contarPor(ventas, (v) => v.plan);
  return Object.keys(conteo).sort((a, b) => conteo[b] - conteo[a] || a.localeCompare(b, 'es'));
}

export function contarPor(ventas: Venta[], clave: (v: Venta) => string): Record<string, number> {
  const out: Record<string, number> = {};
  for (const v of ventas) out[clave(v)] = (out[clave(v)] ?? 0) + planesDeVenta(v);
  return out;
}

/** Ventas de cada bucket (inicio inclusive, fin exclusivo), por ejecutiva. */
export function ventasPorBucket(ventas: Venta[], buckets: Bucket[]): Record<string, number>[] {
  return buckets.map((b) =>
    contarPor(
      ventas.filter((v) => v.fecha >= b.start && v.fecha < b.end),
      (v) => v.ejecutiva,
    ),
  );
}

/** Ventas de los últimos 7 días (hoy incluido) y de los 7 anteriores, para comparar el ritmo. */
export function ultimos7Dias(ventas: Venta[], hoy: string): { actual: number; anterior: number } {
  const desde = addDays(hoy, -6);
  const desdeAnterior = addDays(hoy, -13);
  let actual = 0;
  let anterior = 0;
  for (const v of ventas) {
    if (v.fecha >= desde && v.fecha <= hoy) actual += planesDeVenta(v);
    else if (v.fecha >= desdeAnterior && v.fecha < desde) anterior += planesDeVenta(v);
  }
  return { actual, anterior };
}

/** Tabla ejecutiva × plan con totales por fila. */
export interface FilaEjecutiva {
  ejecutiva: string;
  porPlan: Record<string, number>;
  total: number;
}

export function tablaEjecutivaPlan(ventas: Venta[]): FilaEjecutiva[] {
  return ejecutivasPorVentas(ventas).map((ejecutiva) => {
    const propias = ventas.filter((v) => v.ejecutiva === ejecutiva);
    return { ejecutiva, porPlan: contarPor(propias, (v) => v.plan), total: totalPlanes(propias) };
  });
}
